import { useEffect } from "react";

/**
 * Attaches an IntersectionObserver that toggles `.revealed` on any element
 * with a `data-reveal` attribute as it scrolls into view.
 *
 * Zero-config: sits at the App level, one observer for the whole page.
 * Respects prefers-reduced-motion (elements are already visible via CSS).
 */
export default function useScrollReveal(options = { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
          io.unobserve(entry.target);
        }
      });
    }, options);

    const scan = () => {
      document.querySelectorAll("[data-reveal]:not(.revealed)").forEach((el) => io.observe(el));
    };

    // Initial scan + repeat on DOM mutations (handles route/section changes).
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
